import { useCallback, useState } from 'react';
import axiosInstance from '../redux/api';

/*
 * The four order-editing calls.
 *
 * Every one of them returns the recomputed money state — final_price, vat,
 * delivery_charge, balance_due, suggested_delivery_charge — so the totals are
 * applied straight from the response instead of being re-fetched. That is what
 * makes a quantity change feel immediate.
 *
 * The item *rows* still need a refetch: POST returns only the new
 * order_detail_id, and DELETE flips a line to Cancelled rather than removing
 * it, so neither response describes the resulting list. The pattern is
 * therefore: apply money now, reload rows quietly.
 *
 * Failures are surfaced verbatim. The backend's refusals are written for
 * operators ("cancel the whole order instead", "move it back to an active
 * status first") and are more useful than anything generic invented here.
 */

const MONEY_KEYS = [
  'final_price',
  'total_cost_excluding_vat',
  'vat',
  'delivery_charge',
  'balance_due',
  'suggested_delivery_charge',
];

/** Picks only the money fields, so an edit can't clobber unrelated order data. */
const readMoney = (payload) => {
  const money = {};
  MONEY_KEYS.forEach((key) => {
    if (payload?.[key] !== undefined) money[key] = payload[key];
  });
  return money;
};

const readError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.response?.message ||
  fallback;

export const useOrderEditing = ({ orderId, onMoneyChange, onItemsChange }) => {
  // Keyed by line id, plus 'add' and 'delivery', so each control shows its own
  // spinner rather than freezing the whole card.
  const [pending, setPending] = useState(() => new Set());

  const mark = useCallback((key, busy) => {
    setPending((current) => {
      const next = new Set(current);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const run = useCallback(
    async (key, request, { refreshItems = true } = {}) => {
      mark(key, true);
      try {
        const response = await request();
        const payload = response?.data?.response ?? response?.data ?? {};
        const money = readMoney(payload);
        if (Object.keys(money).length > 0) onMoneyChange?.(money);
        if (refreshItems) await onItemsChange?.();
        return { ok: true, payload };
      } catch (error) {
        return {
          ok: false,
          message: readError(error, 'That change could not be saved.'),
        };
      } finally {
        mark(key, false);
      }
    },
    [mark, onMoneyChange, onItemsChange],
  );

  const addItem = useCallback(
    ({ productId, quantity }) =>
      run('add', () =>
        axiosInstance.post(`admin/orders/${orderId}/items`, {
          product_id: productId,
          quantity,
        }),
      ),
    [orderId, run],
  );

  const setItemQuantity = useCallback(
    ({ detailId, quantity }) =>
      run(`item:${detailId}`, () =>
        axiosInstance.patch(`admin/orders/${orderId}/items/${detailId}`, {
          quantity,
        }),
      ),
    [orderId, run],
  );

  const removeItem = useCallback(
    ({ detailId }) =>
      run(`item:${detailId}`, () =>
        axiosInstance.delete(`admin/orders/${orderId}/items/${detailId}`),
      ),
    [orderId, run],
  );

  // The fee is a money-only change — no line moves, so no row refresh.
  const setDeliveryCharge = useCallback(
    (amount) =>
      run(
        'delivery',
        () =>
          axiosInstance.patch(`admin/orders/${orderId}/delivery-charge`, {
            delivery_charge: Number(amount).toFixed(2),
          }),
        { refreshItems: false },
      ),
    [orderId, run],
  );

  return {
    addItem,
    setItemQuantity,
    removeItem,
    setDeliveryCharge,
    isPending: (key) => pending.has(key),
    isBusy: pending.size > 0,
  };
};

export default useOrderEditing;
